import React, { useMemo } from "react";
import { ArrowDownToLine, Sparkles } from "lucide-react";

const HERO_BG = "https://customer-assets.emergentagent.com/job_caf-neon-experience/artifacts/3nsha4so_file_000000002aa871fdbf8ee46e249013f2.png";
const LOGO = "https://customer-assets.emergentagent.com/job_caf-neon-experience/artifacts/slb6r4tj_file_00000000fe5c61f9b079c9fb85cf0efa.png";

// Particle data generated once at module load (intentionally random, never re-evaluated)
const PARTICLE_DATA = Array.from({ length: 40 }).map((_, i) => ({
  id: i,
  left: Math.random() * 100,
  top: Math.random() * 100,
  size: Math.random() * 3 + 1,
  delay: Math.random() * 8,
  duration: 6 + Math.random() * 10,
  color: Math.random() > 0.5 ? "#00F0FF" : "#FF00E5",
  opacity: 0.3 + Math.random() * 0.5,
}));

function Particles() {
  const particles = useMemo(() => PARTICLE_DATA, []);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {particles.map((p) => (
        <span
          key={p.id}
          className="particle"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: p.color,
            boxShadow: `0 0 ${p.size * 4}px ${p.color}`,
            opacity: p.opacity,
            animation: `float-slow ${p.duration}s ease-in-out ${p.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

export default function Hero() {
  const enter = () => document.getElementById("tracks")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-center justify-center"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center pointer-events-none"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        aria-hidden="true"
      />
      {/* Layered overlays — preserve the sunset's magenta/purple, push contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050508]/40 via-[#050508]/55 to-[#050508] pointer-events-none" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050508]/70 via-transparent to-[#050508]/40 pointer-events-none" aria-hidden="true" />

      {/* Synth grid floor */}
      <div className="absolute inset-x-0 bottom-0 h-2/3 synth-grid opacity-50 pointer-events-none" aria-hidden="true" />

      {/* Particles */}
      <Particles />

      {/* Scan line */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.07]" aria-hidden="true">
        <div className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00F0FF] to-transparent animate-scan-line" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl w-full px-6 md:px-10 py-32 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-7 border border-[#00F0FF]/30 bg-black/40 backdrop-blur-md">
          <Sparkles size={12} className="text-[#00F0FF]" />
          <span className="font-mono-display text-[10px] sm:text-[11px] uppercase tracking-[0.35em] text-[#00F0FF]">
            Album · Coming 2026
          </span>
        </div>

        {/* Logo image — the thorny mark */}
        <div className="relative mx-auto max-w-2xl">
          <img
            src={LOGO}
            alt="Concept And Formality"
            data-testid="hero-logo"
            className="relative w-full h-auto invert brightness-110 drop-shadow-[0_0_30px_rgba(255,0,229,0.5)] drop-shadow-[0_0_60px_rgba(0,240,255,0.3)] animate-pulse-glow"
            style={{ filter: "invert(1) drop-shadow(0 0 25px rgba(255,0,229,0.6)) drop-shadow(0 0 50px rgba(0,240,255,0.35))" }}
          />
        </div>

        <h1 className="sr-only">Concept And Formality — Progressive Synthwave Metal from Las Vegas</h1>

        <p
          data-testid="hero-subtitle"
          className="mt-8 font-mono-display text-sm sm:text-base md:text-lg uppercase tracking-[0.3em] sm:tracking-[0.4em] text-cream-soft/85"
        >
          Progressive · Pop Punk · Metal
        </p>
        <p className="mt-3 font-display text-xs sm:text-sm tracking-[0.5em] text-[#FF00E5] text-glow-magenta">
          FROM LAS VEGAS
        </p>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            data-testid="hero-cta-enter"
            onClick={enter}
            className="btn-shine relative group px-8 py-4 bg-gradient-to-r from-[#FF00E5] to-[#a900b8] text-white font-display font-bold text-xs sm:text-sm uppercase tracking-[0.3em] hover:scale-105 transition-transform glow-magenta animate-pulse-glow"
          >
            Enter The Experience
          </button>
          <button
            data-testid="hero-cta-mosh"
            onClick={() => document.getElementById("mosh")?.scrollIntoView({ behavior: "smooth" })}
            className="px-8 py-4 border border-[#00F0FF]/60 text-[#00F0FF] font-mono-display text-xs uppercase tracking-[0.3em] hover:bg-[#00F0FF]/10 hover:glow-cyan transition-all"
          >
            Join Mosh Team
          </button>
        </div>

        {/* Scroll indicator */}
        <button
          onClick={enter}
          aria-label="Scroll down"
          className="absolute left-1/2 -translate-x-1/2 bottom-8 text-white/50 hover:text-[#00F0FF] transition-colors animate-float-slow"
        >
          <ArrowDownToLine size={20} />
        </button>
      </div>
    </section>
  );
}
